import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../../utils/i18n';
import Button from '@adsk/bim360-matrix-react-button';
import {
  default as TextInput,
  Behaviors as TextInputBehaviors,
  InputUITypes,
  ValidationTypes
} from '@adsk/bim360-matrix-react-text-input';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import {issueTypeInputValidators} from '../../../utils/issueTypes-helpers';
import IssueTypeStatusDropdown from './IssueTypeStatusDropdown';
import IssueTypeStatusSetDropdown from './IssueTypeStatusSetDropdown';
import {presenceValidator} from '../../../utils/validation-helpers';

class IssueTypesAdminCreateModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...this.getModalDefaults(),
      isOpen: props.isOpen,
      cancelFocused: false
    };

    this.createIssueType = this.createIssueType.bind(this);
    this.statusOnChange = this.statusOnChange.bind(this);
    this.statusSetOnChange = this.statusSetOnChange.bind(this);

    this.createIssueTypeInputProps = {
      behavior: TextInputBehaviors.SINGLE_LINE,
      autofocus: true,
      onChange: value => {
        const valid = this.isIssueTypeNameValid(value);
        this.setState({issueTypeName: {isNew: false, value, valid}});
      },
      className: 'IssueTypesAdminCreateModal__textfield'
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.isOpen !== nextProps.isOpen) {
      this.setState({...this.getModalDefaults(), isOpen: nextProps.isOpen});
    }

    if (this.state.isOpen && this.props.isCreating && !nextProps.isCreating) {
      this.closeModal();
    }
  }

  createIssueType() {
    if (this.props.isCreating || !this.state.issueTypeName.valid) {
      return;
    }
    const payload = {
      title: this.state.issueTypeName.value.trim(),
      isActive: this.state.status.value === IssueTypeStatusDropdown.IssueTypeStatuses.ACTIVE.value
    };
    payload.statusSet = this.state.statusSet.value;
    this.props.createIssueTypeAction(payload, {onSuccess: this.props.onCreate});
  }

  getModalDefaults() {
    return {
      issueTypeName: {isNew: true, value: '', valid: false},
      status: IssueTypeStatusDropdown.IssueTypeStatuses.ACTIVE,
      statusSet: IssueTypeStatusSetDropdown.IssueTypeStatusSets.BASIC,
      isOpen: false
    };
  }

  isIssueTypeNameValid(value) {
    const validators = issueTypeInputValidators(this.props.issueTypeNames);
    validators.push({
      message: i18n.t('issueTypes.modal.required'),
      validate: presenceValidator
    });
    return validators.map(validator => validator.validate(value)).every(isValid => isValid);
  }

  getValidationProps() {
    return {
      type: ValidationTypes.SUBTITLE,
      required: true,
      ignore: this.state.issueTypeName.isNew,
      validator: issueTypeInputValidators(this.props.issueTypeNames),
      typeProps: {
        container: this.props.container
      }
    };
  }

  closeModal() {
    this.props.onModalClose();
    this.setState({isOpen: false});
  }

  handleKeyDown(event) {
    // eslint-disable-next-line default-case
    switch (event.keyCode) {
      case 13: // ENTER
        if (!this.props.isCreating && this.state.issueTypeName.valid && !this.state.cancelFocused) {
          event.preventDefault();
          this.createIssueType();
        }
        break;
    }
  }

  statusOnChange(status) {
    this.setState({status});
  }

  statusSetOnChange(statusSet) {
    this.setState({statusSet});
  }
  _renderModalFooter() {
    return (
      <div className="IssueTypesAdminCreateModal__footer">
        <Button
          styleType={Button.StyleType.PRIMARY}
          disabled={this.props.isCreating || !this.state.issueTypeName.valid}
          onClick={this.createIssueType.bind(this)}
          className="IssueTypesAdminCreateModal__button"
          tabIndex="0"
        >
          {this.props.isCreating ? i18n.t('issueTypes.modal.inProgress') : i18n.t('issueTypes.modal.create')}
        </Button>
        <Button
          styleType={Button.StyleType.LINK_SECONDARY}
          onClick={this.closeModal.bind(this)}
          className="IssueTypesAdminCreateModal__button IssueTypesAdminCreateModal__button--cancel"
          tabIndex="0"
          onBlur={() => this.setState({cancelFocused: false})}
          onFocus={() => this.setState({cancelFocused: true})}
        >
          {i18n.t('issueTypes.modal.cancel')}
        </Button>
      </div>
    );
  }

  render() {
    return (
      <SimpleModal
        container={this.props.container}
        title={i18n.t('issueTypes.modal.title')}
        show={this.state.isOpen}
        footer={this._renderModalFooter()}
        onHide={this.closeModal.bind(this)}
        onExited={this.closeModal.bind(this)}
        className="IssueTypesAdminCreateModal"
        modalProps={{
          onKeyDown: this.handleKeyDown.bind(this)
        }}
      >
        {
          <div className="IssueTypesAdminCreateModal__body">
            <div className="IssueTypesAdminCreateModal__text">
              <span>{i18n.t('issueTypes.modal.issueType')}</span>
              <span className="IssueTypesAdminCreateModal__required">*</span>
            </div>
            <TextInput
              value={this.state.issueTypeName.value}
              inputUIType={InputUITypes.NORMAL}
              inputProps={this.createIssueTypeInputProps}
              validationProps={this.getValidationProps()}
            />
            <div className="IssueTypesAdminCreateModal__text">
              <span>{i18n.t('issueTypes.modal.activeInactive')}</span>
              <span className="IssueTypesAdminCreateModal__required">*</span>
            </div>
            <IssueTypeStatusDropdown onChange={this.statusOnChange} selected={this.state.status} />
            <div key="statusSet-title" className="IssueTypesAdminCreateModal__text">
              <span>{i18n.t('issueTypes.header.statusSet')}</span>
              <span className="IssueTypesAdminCreateModal__required">*</span>
            </div>
            <IssueTypeStatusSetDropdown
              key="statusSet-dropdown"
              onChange={this.statusSetOnChange}
              selected={this.state.statusSet}
            />
            <div key="statusSet-infoMessage" className="IssueTypesAdminCreateModal__status-set-info-message">
              {i18n.t('issueTypes.modal.statusSet.infoMessage')}
            </div>
          </div>
        }
      </SimpleModal>
    );
  }
}

IssueTypesAdminCreateModal.displayName = 'IssueTypesAdminCreateModal';

IssueTypesAdminCreateModal.propTypes = {
  container: PropTypes.object.isRequired,
  isCreating: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  createIssueTypeAction: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  issueTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default IssueTypesAdminCreateModal;
